// Theme store.
//
// Хранит исходный выбор пользователя (`'light' | 'dark' | 'system'`) — это
// важно для возможности «вернуться к системной теме». Однако UI-toggle
// переключает только light↔dark; кнопка «Системная» доступна отдельно в
// Settings. См. CLAUDE.md §11.
//
// `resolvedTheme` — фактическая тема, применённая к `<html>` (всегда
// `'light' | 'dark'`). При `theme === 'system'` — берётся
// prefers-color-scheme.
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { persistStorage } from "./_storage";

export type ThemePreference = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

interface ThemeState {
	theme: ThemePreference;
	resolvedTheme: ResolvedTheme;
	setTheme: (theme: ThemePreference) => void;
	toggle: () => void;
	setResolvedTheme: (theme: ResolvedTheme) => void;
}

/**
 * Читает системную тёмную тему. Порядок (см. rules/android-quirks.md):
 *   1. window.__ANDROID_SYSTEM_DARK — выставлено inline-скриптом index.html /
 *      MainActivity.onPageStarted (Capacitor Android).
 *   2. window.AndroidThemeBridge.isSystemDark() — синхронный нативный вызов.
 *   3. matchMedia — web / iOS fallback.
 *
 * В Capacitor WebView matchMedia("(prefers-color-scheme: dark)") ВСЕГДА false
 * (setAlgorithmicDarkeningAllowed=false на Samsung One UI), поэтому нужен мост.
 */
export function readAndroidSystemDark(): boolean {
	if (typeof window === "undefined") return false;
	if (typeof window.__ANDROID_SYSTEM_DARK === "boolean") {
		return window.__ANDROID_SYSTEM_DARK;
	}
	const bridge = window.AndroidThemeBridge;
	if (bridge && typeof bridge.isSystemDark === "function") {
		try {
			return bridge.isSystemDark();
		} catch {
			/* мост недоступен — fallback ниже */
		}
	}
	return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function systemTheme(): ResolvedTheme {
	if (typeof window === "undefined") return "light";
	return readAndroidSystemDark() ? "dark" : "light";
}

export const useThemeStore = create<ThemeState>()(
	persist(
		(set, get) => ({
			theme: "system",
			resolvedTheme: systemTheme(),
			setTheme: (theme) => {
				const resolved: ResolvedTheme = theme === "system" ? systemTheme() : theme;
				set({ theme, resolvedTheme: resolved });
			},
			// UI Toggle переключает только light↔dark. Если был system —
			// фиксируем текущий resolved и инвертируем.
			toggle: () => {
				const { resolvedTheme } = get();
				const next: ResolvedTheme = resolvedTheme === "dark" ? "light" : "dark";
				set({ theme: next, resolvedTheme: next });
			},
			setResolvedTheme: (resolvedTheme) => set({ resolvedTheme }),
		}),
		{
			name: "studyhub:theme",
			storage: persistStorage,
			partialize: (state) => ({ theme: state.theme }),
		},
	),
);
